/**
 * Phase 66 Plan 04 — useUsers (Plan 06 extends with optional `extra`).
 *
 * Lists users (cache-keyed by circuit) for the UsersPage. Distinct from
 * Plan 03's `useUsersCount` which only exposes the count.
 *
 * Plan 06 Task 2 (C-7): optional `extra` arg merged into the useQuery
 * options. CommandPalette passes `{ placeholderData: keepPreviousData }`
 * to avoid flicker during a tenant-switch refetch.
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { listUsers, listCircuitUsers } from "@/api/users";
import { useCircuitId } from "@/hooks/useCircuitId";
import { qk } from "@/lib/queryKeys";
import type { User } from "@/types/api";

type ExtraOpts<TData> = Partial<
  Omit<UseQueryOptions<TData, Error, TData>, "queryKey" | "queryFn">
>;

export function useUsers(extra?: ExtraOpts<User[]>) {
  const circuitId = useCircuitId();
  const tenant = circuitId ?? "all";
  return useQuery({
    queryKey: qk.users(tenant),
    queryFn: () => listUsers(circuitId),
    staleTime: 30_000,
    ...extra,
  });
}

/**
 * Phase 57 CIRCUITO-12 sub-resource per circuit. The cache key derives
 * from `qk.users(circuitId)` so the per-circuit drilldown invalidates in
 * lockstep with the global list.
 */
export function useCircuitUsers(circuitId: string | null | undefined) {
  return useQuery({
    queryKey: [...qk.users(circuitId ?? "all"), "byCircuit", circuitId] as const,
    queryFn: () => listCircuitUsers(circuitId as string),
    enabled: !!circuitId,
    staleTime: 30_000,
  });
}
